/**
 * REST client for the /api/onboarding endpoints.
 *
 * These mirror the FastAPI signatures in cowork/ui/routes/onboarding_routes.py.
 */

import { apiFetch } from "@/api/client";

export type InstallKind = "claude_cli" | "git_for_windows";
export type InstallStatus = "pending" | "running" | "succeeded" | "failed" | "cancelled";

export interface PlatformInfo {
  os: "linux" | "macos" | "windows";
  arch: "x86_64" | "arm64";
  shell: string;
  shell_display: string;
  is_wsl: boolean;
}

export interface ToolCheck {
  installed: boolean;
  path: string | null;
  version: string | null;
  // Only set on the Claude CLI check; distinguishes a system install
  // from the SDK-bundled binary that CoWork ships by default.
  source?: "system" | "sdk_bundled" | "none";
}

export interface NetworkCheck {
  reachable: boolean;
  status?: number;
  message?: string;
}

export interface EnvironmentReport {
  platform: PlatformInfo;
  claude_cli: ToolCheck;
  git: ToolCheck | null;
  winget: ToolCheck | null;
  network: NetworkCheck;
  ready: boolean;
}

export interface InstallJobSummary {
  id: string;
  kind: InstallKind;
  status: InstallStatus;
  started_at: number;
  ended_at: number | null;
  duration_seconds: number;
  exit_code: number | null;
  error_message: string | null;
  log_total_lines: number;
}

export interface InstallStreamResponse extends InstallJobSummary {
  lines: string[];
  cursor: number;
}

// Shape of a credential-verification result. The onboarding connect step
// now verifies the OpenRouter key through ``/settings/verify`` (see
// onboardingStore.verifyToken); this type is the return shape that store
// method adapts to. The old ``/onboarding/token/verify`` and
// ``/onboarding/token/shape`` (Claude-OAuth) endpoints were removed.
export interface TokenVerifyResponse {
  verified: boolean;
  message: string;
  looks_like_token: boolean;
}

export const onboardingApi = {
  getEnvironment: () => apiFetch<EnvironmentReport>("/onboarding/environment"),

  getShell: () =>
    apiFetch<{ shell: string; shell_display: string; os: string }>(
      "/onboarding/shell",
    ),

  startInstall: (kind: InstallKind) =>
    apiFetch<InstallJobSummary>("/onboarding/install", {
      method: "POST",
      body: JSON.stringify({ kind }),
    }),

  pollInstall: (jobId: string, cursor: number) =>
    apiFetch<InstallStreamResponse>(
      `/onboarding/install/${jobId}/stream?cursor=${cursor}`,
    ),

  cancelInstall: (jobId: string) =>
    apiFetch<{ ok: boolean }>(`/onboarding/install/${jobId}/cancel`, {
      method: "POST",
    }),
};
